import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Row,
  Col,
  Card,
  CardBody,
  Alert,
  Badge,
  Input,
  Label,
  Button,
  UncontrolledTooltip
} from 'reactstrap';
import Breadcrumbs from '../../../Components/Common/Breadcrumb';
import TableContainer from '../../../Components/Common/TableContainer';

interface Company {
  id: string;
  company_name: string;
  address_line1: string;
  address_line2?: string;
  city: string;
  state: string;
  zipcode: string;
  country: string;
  phone: string;
  email: string;
  website?: string;
  ein?: string;
  ssn?: string;
  entity_type?: string;
  entity_type_display?: string;
  year_of_formation: number | string;
  full_address?: string;
  created_at: string;
  updated_at: string;
  customer_info?: {
    id: string;
    user_id: number;
    name: string;
    email: string;
    phone: string;
  };
}

const AdminAllCompanies = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [showloader, setShowLoader] = useState(true);
  const [alertMsg, setAlertMsg] = useState<{ open: boolean; type: string; content: string }>({
    open: false,
    type: 'success',
    content: ''
  });

  // Filters
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [selectedState, setSelectedState] = useState<string>('');
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  const fetchAllCompanies = async () => {
    try {
      setShowLoader(true);
      const response = await axios.get('/accounts/companies/', { withCredentials: true });
      if (response.status === 'success') {
        setCompanies(response.companies);
      }
      setShowLoader(false);
    } catch (error: any) {
      setShowLoader(false);
      if (error.response && error.response.status === 403) {
        setAlertMsg({ open: true, type: 'error', content: 'Session expired!' });
        setTimeout(() => (window.location.href = '/logout'), 2000);
      } else {
        setAlertMsg({ open: true, type: 'error', content: 'Error loading companies' });
      }
    }
  };

  useEffect(() => {
    fetchAllCompanies();
  }, []);

  // Get unique countries and states for filters
  const uniqueCountries = useMemo(() => {
    const countries = new Set<string>();
    companies.forEach((c) => {
      if (c.country) countries.add(c.country);
    });
    return Array.from(countries).sort();
  }, [companies]);

  const uniqueStates = useMemo(() => {
    const states = new Set<string>();
    companies.forEach((c) => {
      if (c.state && (!selectedCountry || c.country === selectedCountry)) {
        states.add(c.state);
      }
    });
    return Array.from(states).sort();
  }, [companies, selectedCountry]);

  // Apply filters
  const filteredCompanies = useMemo(() => {
    return companies.filter((company) => {
      if (selectedCountry && company.country !== selectedCountry) return false;
      if (selectedState && company.state !== selectedState) return false;
      return true;
    });
  }, [companies, selectedCountry, selectedState]);

  const resetFilters = () => {
    setSelectedCountry('');
    setSelectedState('');
  };

  const columns = useMemo(
    () => [
      {
        header: 'S.No',
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row, table }: any) => row.index + 1
      },
      {
        header: 'Company Name',
        accessorKey: 'company_name',
        enableColumnFilter: false,
        enableSorting: true
      },
      {
        header: 'Customer',
        accessorKey: 'customer_info.name',
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }: any) => {
          const customer = row.original.customer_info;
          return (
            <div>
              <div className="fw-medium">{customer?.name || 'N/A'}</div>
              <div className="font-size-12 text-muted">{customer?.email || ''}</div>
            </div>
          );
        }
      },
      {
        header: 'Location',
        accessorKey: 'city',
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }: any) => {
          const company = row.original;
          return (
            <div>
              <div>{company.city}, {company.state}</div>
              <div className="font-size-12 text-muted">{company.country}</div>
            </div>
          );
        }
      },
      {
        header: 'Contact',
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: any) => {
          const company = row.original;
          return (
            <div>
              <div className="font-size-12">
                <i className="bx bx-envelope me-1"></i>
                {company.email}
              </div>
              <div className="font-size-12">
                <i className="bx bx-phone me-1"></i>
                {company.phone}
              </div>
            </div>
          );
        }
      },
      {
        header: 'Company Type',
        accessorKey: 'entity_type_display',
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }: any) => {
          const entityType = row.original.entity_type_display;
          return entityType ? (
            <Badge className="badge-soft-info font-size-11">{entityType}</Badge>
          ) : (
            <span className="text-muted">--</span>
          );
        }
      },
      {
        header: 'Year',
        accessorKey: 'year_of_formation',
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }: any) => (
          <div className="text-center">{row.original.year_of_formation}</div>
        )
      },
      {
        header: 'Created',
        accessorKey: 'created_at',
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }: any) => {
          const date = new Date(row.original.created_at);
          return (
            <div className="font-size-12">
              {date.toLocaleDateString('en-US', {
                month: 'short',
                day: '2-digit',
                year: 'numeric'
              })}
            </div>
          );
        }
      }
    ],
    []
  );

  document.title = 'All Companies | BizServe';

  return (
    <div className="page-content">
      <div className="container-fluid">
        {alertMsg.open && (
          <Alert
            color={alertMsg.type === 'success' ? 'success' : 'danger'}
            toggle={() => setAlertMsg({ ...alertMsg, open: false })}
            style={{ fontWeight: 'bold' }}
            fade={false}
          >
            {alertMsg.content}
          </Alert>
        )}

        <div className="loaderContainer" hidden={!showloader}>
          <div className="spinner">
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
          </div>
        </div>

        <Breadcrumbs title="Admin" breadcrumbItem="All Companies" />

        <div className="font-size-13 text-dark mb-3">
          <p>
            This page displays all companies registered by customers across the platform. You can filter by
            location and view detailed information for each company.
          </p>
        </div>

        <Row>
          <Col md={12}>
            <Card className="rounded-4 border">
              <CardBody>
                {/* Filters Toggle */}
                <div className="mb-3">
                  <Button
                    color="outline-primary"
                    size="sm"
                    onClick={() => setFiltersExpanded(!filtersExpanded)}
                    className="d-flex align-items-center"
                  >
                    <i className={`bx ${filtersExpanded ? 'bx-chevron-up' : 'bx-chevron-down'} me-2`}></i>
                    Filters
                  </Button>
                </div>

                {/* Filters */}
                {filtersExpanded && (
                  <div className="mb-3 p-3 border rounded bg-light">
                    <Row className="gy-2">
                      <Col md={3}>
                        <Label className="form-label">Country</Label>
                        <Input
                          type="select"
                          value={selectedCountry}
                          onChange={(e) => {
                            setSelectedCountry(e.target.value);
                            setSelectedState(''); // Reset state when country changes
                          }}
                        >
                          <option value="">All Countries</option>
                          {uniqueCountries.map((country) => (
                            <option key={country} value={country}>
                              {country}
                            </option>
                          ))}
                        </Input>
                      </Col>
                      <Col md={3}>
                        <Label className="form-label">State/Province</Label>
                        <Input type="select" value={selectedState} onChange={(e) => setSelectedState(e.target.value)}>
                          <option value="">All States</option>
                          {uniqueStates.map((state) => (
                            <option key={state} value={state}>
                              {state}
                            </option>
                          ))}
                        </Input>
                      </Col>
                      <Col md={3} className="d-flex align-items-end">
                        <Button color="secondary" size="sm" onClick={resetFilters}>
                          Reset filters
                        </Button>
                      </Col>
                    </Row>
                  </div>
                )}

                <div className="mb-3">
                  <strong>Total Companies: </strong>
                  <Badge color="primary">{filteredCompanies.length}</Badge>
                </div>

                <TableContainer
                  columns={columns}
                  data={filteredCompanies || []}
                  isGlobalFilter={true}
                  isPagination={true}
                  SearchPlaceholder="Search by company name, customer, location..."
                  pagination="pagination"
                  paginationWrapper="dataTables_paginate paging_simple_numbers"
                  tableClass="table-bordered dt-responsive table-nowrap w-100 dataTable no-footer dtr-inline"
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default AdminAllCompanies;

